import { Box, Button, Flex } from '@chakra-ui/react'
import { userListContext } from 'BrokerTsLib/src/context'
import type { UserPagedQuery } from 'BrokerTsLib/src/dtos'
import { useSearchUsers } from 'BrokerTsLib/src/hooks/useUser'
import { noop } from 'lodash'
import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router'

import { Add } from '../../icons'
import { PageToolbar } from '../PageToolbar'
import { SectionHeading } from '../shared/SectionHeading'
import { ValidityBadge } from '../shared/ValidityBadge'
import {
  UserList,
  UserListEmailColumn,
  UserListNameColumn,
  UserListRolesColumn,
  UserListTypeColumn,
  UserListUserNameColumn,
  UserListValidityColumn,
} from '../users/UserList'
import { UserListFilter } from '../users/UserListFilter'

export const OrganizationUserList = ({
  organizationUnitId,
  effectivelyValid,
}: {
  organizationUnitId: number
  effectivelyValid: boolean
}) => {
  const navigate = useNavigate()

  const [filter, setFilter] = useState<UserPagedQuery>({
    organizationUnitId,
    name: '',
    showInvalid: false,
    pageIndex: 1,
    showAllItems: true,
  })

  const { data, refetch, isLoading } = useSearchUsers(filter)
  const items = useMemo(() => data?.data.items ?? [], [data])

  useEffect(() => {
    setFilter((value) => ({
      ...value,
      organizationUnitId,
      name: '',
      showInvalid: !effectivelyValid,
      pageIndex: 1,
    }))
  }, [organizationUnitId])

  return (
    <Box sx={{ display: 'grid', gap: 4 }}>
      <userListContext.Provider
        value={{
          loading: isLoading,
          totalCount: data?.data?.totalCount ?? 0,
          currentPage: filter.pageIndex,
          pageSize: items.length,
          filter: filter,
          setCurrentPage: (p) => setFilter((f) => ({ ...f, pageIndex: p })),
          setPageSize: noop,
          updateFilter: (f) => setFilter(f),
          reload: () => refetch(),
        }}
      >
        <PageToolbar
          leftElements={<SectionHeading>Uživatelé v organizaci</SectionHeading>}
          rightElements={
            <Flex sx={{ gap: 4 }}>
              <Button
                colorScheme="primary"
                size="sm"
                onClick={() =>
                  navigate(`/organization/${organizationUnitId}/createUser`)
                }
              >
                <Add />
                Vytvořit nového uživatele
              </Button>
            </Flex>
          }
        />

        <UserListFilter hideInactiveFilter={!effectivelyValid} />
        <UserList
          users={items}
          columnsDefinition={[
            UserListNameColumn,
            UserListUserNameColumn,
            UserListEmailColumn,
            {
              ...UserListValidityColumn,
              cellExpr: (item) =>
                effectivelyValid ? (
                  <ValidityBadge valid={item.valid} />
                ) : (
                  <ValidityBadge valid={false} />
                ),
            },
            UserListTypeColumn,
            UserListRolesColumn,
          ]}
          sx={{ boxShadow: 'card' }}
        />
      </userListContext.Provider>
    </Box>
  )
}
